// なぞる問題の判定・補助ロジック
// SVG path をブラウザの SVGGeometryElement.getPointAtLength で点列化、
// ユーザー軌跡との距離比較で正誤を判定する。

export type Point = { x: number; y: number };

// 一時 SVG path を作って path 文字列から N 点をサンプル
export function samplePath(pathStr: string, count = 50): Point[] {
  const svgNS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(svgNS, "svg");
  svg.setAttribute("style", "position:absolute;visibility:hidden;width:0;height:0;");
  const pathEl = document.createElementNS(svgNS, "path");
  pathEl.setAttribute("d", pathStr);
  svg.appendChild(pathEl);
  document.body.appendChild(svg);

  const totalLength = pathEl.getTotalLength();
  const points: Point[] = [];
  for (let i = 0; i <= count; i++) {
    const p = pathEl.getPointAtLength(totalLength === 0 ? 0 : (i / count) * totalLength);
    points.push({ x: p.x, y: p.y });
  }
  document.body.removeChild(svg);
  return points;
}

// ストロークの開始点・終了点・初期方向ベクトル（書き順矢印に使う）
export function getStrokeMeta(pathStr: string): {
  start: Point;
  end: Point;
  direction: Point;
} {
  const points = samplePath(pathStr, 20);
  const start = points[0];
  const end = points[points.length - 1];
  const dirRef = points[Math.min(5, points.length - 1)];
  const dx = dirRef.x - start.x;
  const dy = dirRef.y - start.y;
  const len = Math.hypot(dx, dy) || 1;
  return { start, end, direction: { x: dx / len, y: dy / len } };
}

export type JudgeResult =
  | { ok: true }
  | { ok: false; reason: "too_short" | "wrong_start" | "off_path" | "too_short_path" };

// ユーザー軌跡が手本ストロークと一致しているかを判定
// viewBox 0-109 を想定した tolerance 値
export function judgeTrace(
  userPath: Point[],
  modelPathStr: string,
  options: { tolerance?: number; startTolerance?: number; minLengthRatio?: number } = {}
): JudgeResult {
  const tolerance = options.tolerance ?? 40;
  const startTolerance = options.startTolerance ?? 50;
  const minLengthRatio = options.minLengthRatio ?? 0.3;

  if (userPath.length < 3) return { ok: false, reason: "too_short" };

  const modelPoints = samplePath(modelPathStr, 60);
  const modelStart = modelPoints[0];

  const startDist = Math.hypot(
    userPath[0].x - modelStart.x,
    userPath[0].y - modelStart.y
  );
  if (startDist > startTolerance) return { ok: false, reason: "wrong_start" };

  let maxDist = 0;
  for (const up of userPath) {
    let minDist = Infinity;
    for (const mp of modelPoints) {
      const d = Math.hypot(up.x - mp.x, up.y - mp.y);
      if (d < minDist) minDist = d;
    }
    if (minDist > maxDist) maxDist = minDist;
  }
  if (maxDist > tolerance) return { ok: false, reason: "off_path" };

  let userLength = 0;
  for (let i = 1; i < userPath.length; i++) {
    userLength += Math.hypot(
      userPath[i].x - userPath[i - 1].x,
      userPath[i].y - userPath[i - 1].y
    );
  }
  let modelLength = 0;
  for (let i = 1; i < modelPoints.length; i++) {
    modelLength += Math.hypot(
      modelPoints[i].x - modelPoints[i - 1].x,
      modelPoints[i].y - modelPoints[i - 1].y
    );
  }
  if (userLength < modelLength * minLengthRatio)
    return { ok: false, reason: "too_short_path" };

  return { ok: true };
}

// ユーザーの pointer 位置を手本ストロークの最近点に snap する
// progress を進める方向のみ移動（逆戻りしない）。pointer が遠すぎると進めない。
export function snapPointToPath(
  point: Point,
  modelPoints: Point[],
  lastSnapIdx: number,
  options: { maxDist?: number; searchAhead?: number } = {}
): { snapped: Point; idx: number; advanced: boolean } {
  const maxDist = options.maxDist ?? 35;
  const searchAhead = options.searchAhead ?? 20; // 先読み範囲（点数）

  let minDist = Infinity;
  let bestIdx = lastSnapIdx;
  const end = Math.min(modelPoints.length - 1, lastSnapIdx + searchAhead);
  for (let i = lastSnapIdx; i <= end; i++) {
    const d = Math.hypot(point.x - modelPoints[i].x, point.y - modelPoints[i].y);
    if (d < minDist) {
      minDist = d;
      bestIdx = i;
    }
  }
  if (minDist > maxDist) {
    return { snapped: modelPoints[lastSnapIdx], idx: lastSnapIdx, advanced: false };
  }
  return {
    snapped: modelPoints[bestIdx],
    idx: bestIdx,
    advanced: bestIdx > lastSnapIdx,
  };
}

// 点列を smooth な SVG path 文字列に変換（Catmull-Rom近似で滑らかに）
export function pointsToSvgPath(points: Point[]): string {
  if (points.length === 0) return "";
  if (points.length === 1) return `M${points[0].x},${points[0].y}`;
  let d = `M${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L${points[i].x},${points[i].y}`;
  }
  return d;
}
